// frontend/src/index.js
// デモモード時のadapter差し替えは、他サービスのaxiosインスタンス生成より前に行う必要があるため最初にimportする
import './demo/installDemoAdapter';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { LOG_API_URL } from './config';
import { initPostHog } from './utils/posthog';

// PostHog セッションリプレイ・分析を初期化
if (process.env.REACT_APP_POSTHOG_API_KEY) {
  initPostHog();
}

// ログの保存場所を表示
console.info('ログファイルは以下の場所に保存されています:');
console.info('- バックエンドログ: /logs/backend.log');
console.info('- エラーログ: /logs/backend-error.log');
console.info('- ユーザーアクションログ: /logs/user-actions-[日付].log');
console.info('- 例外ログ: /logs/backend-exceptions.log');
console.info('- ウェブアプリケーションルートディレクトリの「logs」フォルダを確認してください');

// バックエンドログAPIのURL
const LOG_API_ENDPOINT = LOG_API_URL;

// ESLintエラーと未処理のJSエラーを検出するためのスクリプト
document.addEventListener('DOMContentLoaded', () => {
  // 再帰的なログ記録を防止するフラグ
  let isLogging = false;
  let compilationErrorDetected = false;

  // 安全にエラーをログに記録する関数
  const safeLogError = (type, message, details = null) => {
    if (isLogging) return;
    
    try {
      isLogging = true;
      
      // バックエンドのログAPIに送信
      try {
        const logData = {
          level: 'ERROR',
          type: type,
          message: message,
          details: details,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        };
        
        fetch(LOG_API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(logData),
          keepalive: true
        }).catch(e => {
          // ログの送信失敗は表示しない（軽微なエラー）
        });
      } catch (e) {
        // ログ記録中のエラーも表示しない（軽微なエラー）
      }
    } finally {
      isLogging = false;
    }
  };

  // エラーオブジェクトから有用な情報を抽出
  const extractErrorInfo = (error) => {
    if (!error) return { message: 'Unknown error' };
    
    // Error オブジェクトの場合
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
        // 追加プロパティがあれば抽出
        ...(typeof error === 'object' ? error : {})
      };
    }
    
    // オブジェクトの場合
    if (typeof error === 'object') {
      try {
        // 循環参照を避けるための安全な文字列変換
        return JSON.parse(JSON.stringify(error, (key, value) => {
          if (key === 'target' || key === 'currentTarget') return undefined;
          return value;
        }));
      } catch (e) {
        return { message: String(error) };
      }
    }
    
    // その他の型
    return { message: String(error) };
  };

  // console.errorのオーバーライド
  const originalConsoleError = console.error;
  console.error = function(...args) {
    // 元のconsole.errorを呼び出し
    originalConsoleError.apply(console, args);
    
    // 既にログ記録中なら何もしない（無限ループ防止）
    if (isLogging) return;
    
    try {
      // React特有の警告を無視（多すぎるため）
      const message = args[0] && typeof args[0] === 'string' ? args[0] : '';
      if (
        message.includes('Warning: ') || 
        message.includes('React does not recognize') ||
        message.includes('Invalid DOM property') ||
        message.includes('Failed prop type')
      ) {
        return;
      }
      
      // コンパイルエラーは一度だけログに記録
      if (message.includes('Failed to compile') && compilationErrorDetected) {
        return;
      }
      
      if (message.includes('Failed to compile')) {
        compilationErrorDetected = true;
      }
      
      // エラーを安全にログに記録
      safeLogError('console_error', 'Console Error', {
        arguments: args.map(arg => extractErrorInfo(arg)),
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      });
    } catch (e) {
      // エラーのログ記録中にエラーが発生した場合、何もしない
    }
  };
  
  // コンパイルエラーを検出するための監視関数
  setInterval(() => {
    try {
      const errorOverlay = document.querySelector('iframe[title="Error Overlay"]');
      if (errorOverlay && !compilationErrorDetected) {
        compilationErrorDetected = true;
        
        // エラー内容をできるだけ取得
        let errorContent = 'コンパイルエラーが検出されました';
        try {
          if (errorOverlay.contentDocument && errorOverlay.contentDocument.body) {
            errorContent = errorOverlay.contentDocument.body.textContent || errorContent;
          }
        } catch (e) {
          // クロスオリジンなどでアクセスできない場合は無視
        }
        
        safeLogError('compilation_error', 'Compilation Error', {
          content: errorContent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
      }
    } catch (e) {
      // エラーは無視
    }
  }, 3000);
  
  // 未処理のJavaScriptエラーをキャプチャ（index.html から app:error として再発火される）
  window.addEventListener('app:error', (event) => {
    if (isLogging) return;

    try {
      const detail = event.detail || {};
      const error = detail.error || new Error(detail.message || 'Unknown error');
      const errorInfo = {
        message: detail.message || error.message,
        filename: detail.filename || 'unknown',
        lineno: detail.lineno,
        colno: detail.colno,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        type: error.name || 'Error'
      };

      safeLogError('unhandled_error', 'Unhandled JavaScript Error', errorInfo);
    } catch (e) {
      // エラーは無視
    }
  });

  // 未処理のPromiseリジェクションをキャプチャ（index.html から app:unhandledrejection として再発火される）
  window.addEventListener('app:unhandledrejection', (event) => {
    if (isLogging) return;

    try {
      const reason = event.detail?.reason;
      const rejectionInfo = {
        reason: extractErrorInfo(reason),
        timestamp: new Date().toISOString(),
        url: window.location.href
      };

      safeLogError('promise_rejection', 'Unhandled Promise Rejection', rejectionInfo);
    } catch (e) {
      // エラーは無視
    }
  });
  
  // React用のエラーバウンダリハンドラ
  window.logReactError = (error, errorInfo) => {
    if (isLogging) return;
    
    try {
      const reactErrorInfo = {
        error: extractErrorInfo(error),
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href
      };
      
      safeLogError('react_error', 'React Component Error', reactErrorInfo);
    } catch (e) {
      // エラーは無視
    }
  };
});



const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(data => {
  // 重要なパフォーマンス指標もログに記録
  if (data.value > 1000) { // 1秒以上かかった指標のみ記録

  }
});
